/**
 * ChatDrawer — the AI chat, hosted left of the nav rail (shell v3).
 *
 * Chat sits outside the stage because the rail renders between it and the
 * panels. The stage derives its pixel budget from its own box, so widening the
 * drawer shrinks panels and then hides the rightmost ones.
 *
 * ── Open / close is a slide ────────────────────────────────────────────────
 * Three nested boxes, because the element that animates and the element that
 * must hold still are not the same one:
 *
 *   wrap   flex item; its WIDTH eases 0 ⇄ --chat-w. This is what pushes the
 *          rail and the stage aside, so the drawer takes its room as it opens
 *          instead of appearing in a gap that was already sitting there.
 *   clip   overflow:hidden, so the part of the panel that has not arrived yet
 *          is cut at the drawer's edge rather than drawn over the rail.
 *   slide  pinned to clip's RIGHT edge at a constant --chat-w. Its left edge
 *          therefore sits at -(--chat-w) while wrap is collapsed and at 0 once
 *          wrap is full: the panel slides in from off-screen left.
 *
 * The width animation lives entirely on `wrap`. `slide` never changes width,
 * so nothing inside it reflows mid-animation — no re-wrapping transcript, no
 * 10cqw gutters recomputed per frame, and no scroll-anchor correction firing
 * sixty times (Panel.jsx watches its scroll port's width for exactly that).
 *
 * The drawer carries a rule on its right edge (Figma 1744:19583): the chat
 * panel is `transparent`, so the rule is what marks where the transcript ends
 * and the rail begins.
 *
 * A registered panel: open/closed state rides `openPanels`, so deep links and
 * the agent's open_panel reach it, and it renders through PanelHost for the
 * transparent surface, the floating ×, the error boundary and PanelContext.
 * Explicit left/right styles make it span the wrapper up to the drawer rule.
 */

import { useAtomValueRawSync, useSetAtom } from 'jotai';
import type { CSSProperties, ReactElement, RefObject } from 'react';
import React from 'react';

import { CHAT_DRAWER_PANEL_STYLE } from '../panelGeometry';
import { PanelHost } from '../PanelHost';
import { panels as registry } from '../registry';
import { cn } from '@/lib/utils';
import { chatWidthAtom, setChatWidthAtom } from '@/state/ui/shellRecordAtoms';
import type { PanelHostBindings, PanelId } from '@/types/panels';
import { PanelTransitionState } from '@/types/panels';

import { CHAT_SLIDE_MS } from './slideMotion';
import { useChatResize } from './useChatResize';
import { useChatSlideMotion } from './useChatSlideMotion';

/** The committed width and slide duration, as the custom properties its CSS reads. */
interface DrawerStyle extends CSSProperties {
    '--chat-w': string;
    '--chat-slide-ms': string;
}

interface ChatDrawerProps {
    /** False while the drawer is playing its exit, still mounted but posed closed. */
    open?: boolean;
    /**
   * The slide's own box, and required. useChatSlide owns it: the open pose is
   * set in advance through this ref, the drag writes its preview to it, and the slide
   * listeners scope the live-follow window by its transition events.
   */
    wrapRef: RefObject<HTMLDivElement | null>;
    panelProps: Record<string, unknown>;
    bindings: PanelHostBindings;
    onResizeChange?: (isResizing: boolean) => void;
    onSlideMotion?: (isMoving: boolean) => void;
}

/** The chat's registry id, shared with AppShell so neither spells it. */
export const CHAT_PANEL_ID: PanelId = 'ai_chat';

export const ChatDrawer = ({
    open: isOpen = true,
    wrapRef,
    panelProps,
    bindings,
    onResizeChange,
    onSlideMotion,
}: ChatDrawerProps): ReactElement | null => {
    const entry = registry[CHAT_PANEL_ID];
    const width = useAtomValueRawSync(chatWidthAtom);
    const setWidth = useSetAtom(setChatWidthAtom);
    useChatSlideMotion(wrapRef, onSlideMotion);
    const { onPointerDown } = useChatResize({ wrapRef, open: isOpen, width, setWidth,
        onResizeChange });

    if (!entry) {
        return null;
    }
    const { Component, config } = entry;
    const wrapStyle: DrawerStyle = {
        '--chat-w': `${width}px`,
        '--chat-slide-ms': `${CHAT_SLIDE_MS}ms`,
    };

    return (
        <div
            ref={wrapRef}
            data-open={isOpen || undefined}
            style={wrapStyle}
            className={cn(
                'ai-chat-drawer relative shrink-0 [width:0px]',
                'data-[open]:[width:var(--chat-live-w)]',
            )}
        >
            {/* Clips whatever has not arrived yet. Separate from `wrap`: the resize
          strip sits outside this box (at left-full) and must not be clipped. */}
            <div className="absolute inset-0 overflow-hidden">
                {/* The constant-width box. Pinned right, so a collapsing `wrap` walks
            it off to the left instead of squeezing it. */}
                <div className="absolute inset-y-0 right-0 [width:var(--chat-live-w)]">
                    <PanelHost
                        id={CHAT_PANEL_ID}
                        config={config}
                        bindings={bindings}
                        transitionState={PanelTransitionState.Stable}
                        willLayoutAnimate={false}
                        isPanelTransitionOver
                        style={CHAT_DRAWER_PANEL_STYLE}
                    >
                        <Component {...panelProps} />
                    </PanelHost>

                    {/* Right-edge rule, inset by the panels' own --pad so its ends line
              up with the panel tops and bottoms rather than the window edges.
              The panel behind it reaches this same line (see .ai-chat-drawer in
              panels.css), so the transcript's scrollbar lands against the rule
              instead of --pad away from it. */}
                    <span
                        aria-hidden="true"
                        className={cn(
                            'pointer-events-none absolute right-0 bg-occ-border-1',
                            '[width:var(--ai-chat-rule-width)] [top:var(--pad)]',
                            '[bottom:calc(var(--pad)_+_env(safe-area-inset-bottom,0px))]',
                        )}
                    />
                </div>
            </div>

            {/* Grab strip for the drag, parked entirely to the RIGHT of the rule
          (left-full = the drawer's outer edge), so the resize cursor never
          covers the transcript and the scrollbar riding its edge. Gone
          mid-slide: the edge it grabs is still moving, so a drag started there
          would resize from a width the user never saw. */}
            {isOpen && (
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize AI chat"
                    onPointerDown={onPointerDown}
                    className="absolute inset-y-0 left-full z-10 w-2 cursor-col-resize touch-none"
                />
            )}
        </div>
    );
};
